import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import type { ITicket, ITicketMessage } from "./proto";

const SUBJECT_LENGTH = 60;

export type ConversationEntry = { message: ITicketMessage; topic?: string };

export type ConversationData = {
  latestTicketId: string | null;
  openTicketIds: string[];
  entries: ConversationEntry[];
};

function byCreated(a: { createdAt?: string | null }, b: { createdAt?: string | null }): number {
  return (a.createdAt ?? "").localeCompare(b.createdAt ?? "");
}

function entriesOf(ticket: ITicket): ConversationEntry[] {
  const messages = (ticket.messages ?? []).slice().sort(byCreated);
  const startedByTeam = messages[0] && messages[0].authorRole !== "customer";
  return messages.map((message, i) => ({ message, topic: i === 0 && startedByTeam ? (ticket.subject ?? undefined) : undefined }));
}

async function loadConversation(parcelId: string): Promise<ConversationData> {
  const tickets = (await api.listTickets(parcelId)).slice().sort(byCreated);
  const threads = await Promise.all(tickets.map((ticket) => api.getTicket(parcelId, ticket.id!)));
  return {
    latestTicketId: tickets.length ? tickets[tickets.length - 1].id! : null,
    openTicketIds: tickets.filter((ticket) => ticket.status !== "RESOLVED").map((ticket) => ticket.id!),
    entries: threads.flatMap(entriesOf),
  };
}

export function useConversation(parcelId: string) {
  return useQuery({ queryKey: ["conversation", parcelId], queryFn: () => loadConversation(parcelId) });
}

export function subjectFrom(text: string): string {
  const firstLine = text.split("\n")[0].replace(/\s+/g, " ").trim();
  return firstLine.length > SUBJECT_LENGTH ? `${firstLine.slice(0, SUBJECT_LENGTH - 1)}…` : firstLine;
}
